import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { useMediaQuery } from 'react-responsive';
import { Button, Collapse, Column, Container, Icon, List, Row } from '@pella/sash-framework';
import { useGlobalConfig } from '../../../../hooks/use-global-config';
import { useShowroomUrls } from '../../../../hooks/use-showroom-urls';
import { formatPhoneNumber } from '../../../utilities/functions';
import { isValidArray } from '../../../utilities/validations';

import StaticMap from '../../../common/static-map';
import CustomerShowroomText from '../customer-showroom-text';
import CardRenderer from '../../../common/card-types/card-renderer';

const ShowroomPanel = ({ showroomData, className, showFaq, view = 'panel', isCaseStudy }) => {
	const { branch_location } = useGlobalConfig();
	const { newShowrooms, oldShowrooms } = useShowroomUrls();
	const [showroom, setShowroom] = useState(showroomData);
	const isMobile = useMediaQuery({ maxWidth: 767 });

	useEffect(() => {
		if (showroomData?.store_number) {
			const showroomInfo = newShowrooms?.nodes?.find((show) => show.showroom_id === showroomData.store_number) || oldShowrooms?.nodes?.find((show) => show.showroom_id === showroomData.store_number);
			if (showroomInfo?.url) {
				setShowroom({ ...showroomData, url: showroomInfo.url, heading: showroomInfo.page_heading?.replace(/<\/?span[^>]*>/g, '') || showroomInfo.name });
			}
		}
	}, [showroomData, newShowrooms.nodes, oldShowrooms.nodes]);

	const mapAddress = `${showroomData?.line_1}${showroomData?.line_2 != null ? '+' + showroomData?.line_2 : ''}+${showroomData?.city},+${showroomData?.state}+${showroomData?.postal_code}`;

	return (
		<div className={`showroomPanel relative z-1 py-4 ${view === 'panel' ? 'bg-gray-5' : 'mt-6'} ${className || ''}`}>
			<Container
				width={1000}
				noPadding={view === 'truncated'}
			>
				{branch_location?.heading && <p className='text-h3 mb-4'>{branch_location.heading}</p>}

				<Row
					className='gap-4'
					noGutters
				>
					<Column
						xs={12}
						md={view === 'truncated' ? null : 'fluid'}
					>
						<StaticMap
							mapLocation={mapAddress}
							zoom={12}
							className='aspect-[4/3.5] md:!h-full'
							title={`Google map location for ${showroomData?.name}`}
						/>
					</Column>

					<Column
						xs={12}
						md={view === 'truncated' ? null : 'fluid'}
					>
						<div className='md:border md:border-gray-42 md:bg-white md:p-5'>
							<div className='border border-gray-42 bg-white p-5 md:border-0 md:p-0'>
								{showroom?.store_number && (
									<div className='mb-6 flex flex-row'>
										<Icon
											name='location_on'
											className='mt-1 text-black'
										/>

										<div className='ml-1'>
											{showroom?.url ? (
												<Link
													className='link-underline block font-semibold uppercase tracking-0.5px md:text-base'
													to={showroom.url}
												>
													{showroom.heading}
												</Link>
											) : (
												showroom.heading
											)}

											<p className='m-0 mt-2 text-xs text-black'>
												{showroom.line_1 && <span className='block'>{showroom.line_1}</span>}
												{showroom.line_2 && <span className='block'>{showroom.line_2}</span>}
												<span className='block'>
													{showroom.city}, {showroom.state} {showroom.postal_code}
												</span>
											</p>

											{showroom.phone && <p className='m-0 text-xs text-black'>{formatPhoneNumber(showroom.phone)}</p>}
										</div>
									</div>
								)}

								{branch_location?.location_services?.heading && <p className='mb-2.5 pl-7 text-sm font-bold'>{branch_location.location_services.heading}</p>}

								{isValidArray(branch_location?.location_services?.entries) && (
									<List
										bullets='none'
										className='space-y-2 pl-7'
									>
										<li>
											<CustomerShowroomText
												showroomData={showroomData}
												infoPosition='right'
											/>
										</li>

										{branch_location.location_services.entries.map((service, index) => (
											<li
												key={index}
												className='flex items-center text-sm'
											>
												{service.material_symbol && (
													<Icon
														name={service.material_symbol}
														className='mr-2 text-black'
													/>
												)}
												{service.heading && <span dangerouslySetInnerHTML={{ __html: service.heading }} />}
											</li>
										))}
									</List>
								)}

								{((isMobile && view === 'panel') || view === 'truncated') && showFaq && isValidArray(branch_location?.faq?.cards) && (
									<Collapse className='mt-4 border !border-gray-42 bg-white'>
										<Collapse.Trigger className='!border-0 pl-6 !text-sm'>{branch_location.faq.heading}</Collapse.Trigger>
										<Collapse.Content className='px-6'>
											<Row className='!-mx-1 [&>*]:!p-1'>
												{branch_location.faq.cards.map((card, index) => (
													<Column
														key={index}
														xs={12}
													>
														<CardRenderer data={card} />
													</Column>
												))}
											</Row>
										</Collapse.Content>
									</Collapse>
								)}
							</div>

							<Button
								href={isCaseStudy ? '/professionals/request-project-quote/' : '/where-to-buy/schedule-consultation/'}
								color='primary'
								className='mt-4'
								wide
							>
								{isCaseStudy ? 'Get A Quote' : 'Schedule Home Consultation'}
							</Button>
						</div>
					</Column>
				</Row>

				{!isMobile && view === 'panel' && showFaq && isValidArray(branch_location?.faq?.cards) && (
					<Collapse className='mt-4 border !border-gray-42 bg-white'>
						<Collapse.Trigger className='!border-0 pl-6'>{branch_location.faq.heading}</Collapse.Trigger>
						<Collapse.Content className='px-6'>
							<Row className='!-mx-1 [&>*]:!p-1'>
								{branch_location.faq.cards.map((card, index) => (
									<Column
										key={index}
										xs={12}
										md='fluid'
									>
										<CardRenderer data={card} />
									</Column>
								))}
							</Row>
						</Collapse.Content>
					</Collapse>
				)}
			</Container>
		</div>
	);
};

ShowroomPanel.propTypes = {
	showroomData: PropTypes.object.isRequired,
	className: PropTypes.string,
	showFaq: PropTypes.bool,
	view: PropTypes.string,
	isCaseStudy: PropTypes.bool,
};

export default ShowroomPanel;
